import React, {useEffect, useState, useCallback} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../hooks/hooks';
import {deleteProjectType, getProjectTypes} from '../../../../features/types';
import { SVGS } from '../../../../assets/images';
import {TiminatorType} from '../../../../features/types/types';
import IconTextLarge from '../../../../components/shared/large-icon-text';
import TiminatorTableV2 from '../../../../components/shared/table-v2';
import { useNavigate } from 'react-router-dom';
import PageWrapperV2 from '../../../../components/shared/page-wrapper-v2';
import PageHeader from '../../../../components/shared/page-header';
import { useIntl } from 'react-intl';
import CreateNewButtonV2 from '../../../../components/shared/create-new-button-v2';
import { DeleteDialog } from '../../../../components/shared/delete-dialog';
import PageBody from '../../../../components/shared/page-body';
import { Lx } from '../../../../i18n/consts';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import { useMediaQuery } from 'react-responsive';
import { TypeFilter, DEFAULT_TYPE_FILTER_STATE } from '../type-filter';
import { DeleteIconButton } from '../../../../components/shared/delete-icon-button';
import PageFooter from '../../../../components/shared/page-footer';
import { useProjectTypesTable } from './hook/useProjectTypesTable';
import { TypeHeader } from '../type-header';
import { useInfinitePagination } from '../../../../hooks/useInfinitePagination';
import { canManageProjectTypes, canSeeProjectTypesSection } from '../../../../features/profile/entityPermissions';
import { getVisibleTypeTabs } from '../../tabs';

export default function ProjectTypes() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const [selectedProjectType, setSelectedProjectType] = useState<TiminatorType | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<number>(-1);
    const [search, setSearch] = useState('');
    const isMobile = useMediaQuery({ maxWidth: 1000  });

    const projectTypesState = useAppSelector(store => store.types.projects);
    const typesState = useAppSelector(store => store.types);
    const profile = useAppSelector(store => store.profile);
    const hasMore = projectTypesState.items.length < projectTypesState.total;
    const canEditProjectTypes = canManageProjectTypes(profile, projectTypesState.permissions);
    const canViewProjectTypes = canSeeProjectTypesSection(profile, projectTypesState.permissions);

    const fetchProjectTypesPage = useCallback((page: number) => {
        return dispatch(getProjectTypes(page)).unwrap();
    }, [dispatch]);

    const { loadInitial, loadNext, isLoadingMore, isLoadingInitial, reset } = useInfinitePagination({
        hasMore,
        fetchPage: fetchProjectTypesPage,
    });

    const [mobileView, setMobileView] = useState<'list' | 'filter'>('list');

    const handleMobileFilterClick = () => {
        setMobileView(prev => (prev === 'list' ? 'filter' : 'list'));
    };

    const handleMobileFilterClose = useCallback(() => {
        setMobileView('list');
    }, []);

    const handleFilterChange = useCallback(({ search: nextSearch, selectedStatus: nextStatus }: { search: string; selectedStatus: number }) => {
        setSearch(nextSearch);
        setSelectedStatus(nextStatus);
    }, []);

    const handleMobileHeaderClose = useCallback(() => {
        handleFilterChange(DEFAULT_TYPE_FILTER_STATE);
    }, [handleFilterChange]);

    useEffect(() => {
        if (!canViewProjectTypes) {
            const availableTab = getVisibleTypeTabs(profile, {
                project: projectTypesState.permissions,
                shift: typesState.shifts.permissions,
                absence: typesState.absences.permissions,
                tag: typesState.tags.permissions,
            })[0];

            if (availableTab) {
                navigate(availableTab.path, { replace: true });
            }
        }
    }, [canViewProjectTypes, navigate, profile, projectTypesState.permissions, typesState.absences.permissions, typesState.shifts.permissions, typesState.tags.permissions]);

    useEffect(() => {
        if (!canViewProjectTypes) {
            return;
        }

        loadInitial().catch(() => { /* handled elsewhere */ });
    }, [canViewProjectTypes, loadInitial]);

    useEffect(() => {
        if (!isMobile) {
            setMobileView('list');
        }
    }, [isMobile]);

    const { columns, data, isEmpty } = useProjectTypesTable(
        search,
        selectedStatus,
        isMobile
    );

    if (!canViewProjectTypes) {
        return null;
    }

    const showEmptyState = !isLoadingInitial && isEmpty;

    const enhancedColumns = columns
        .filter(col => canEditProjectTypes || col.id !== 'deleteButton')
        .map(col => {
            if (col.id === 'deleteButton') {
                return {
                    ...col,
                    format: (rowData: TiminatorType) => (
                        <DeleteIconButton
                            onClick={() => {
                                setSelectedProjectType(rowData);
                                setOpen(true);
                            }}
                        />
                    ),
                };
            }
            return col;
        });

    function onRowEdit(projectType: TiminatorType) {
        if (!canEditProjectTypes) {
            return;
        }

        navigate(NAVIGATION_ROUTES.EDIT_PROJECT_TYPE, {
            state: { projectType }
        });
    }

    const handleDeleteCancel = () => {
        setOpen(false);
        setSelectedProjectType(null);
    };

    const handleDeleteConfirm = () => {
        if (selectedProjectType) {
            dispatch(deleteProjectType(selectedProjectType.id)).unwrap().then(() => {
                reset();
                loadInitial().catch(() => { /* handled elsewhere */ });
            });
        }
        handleDeleteCancel();
    };

    const handleCreateNew = () => {
        if (!canEditProjectTypes) {
            return;
        }

        navigate(NAVIGATION_ROUTES.CREATE_PROJECT_TYPE);
    }

    return <>
        <PageWrapperV2>
            <PageHeader>
                <TypeHeader
                    isEmpty={showEmptyState}
                    isFilterOpen={mobileView === 'filter'}
                    onMobileFilterClick={handleMobileFilterClick}
                    onCreateNew={handleCreateNew}
                    canCreate={canEditProjectTypes}
                    onMobileFilterClose={handleMobileHeaderClose}
                    loading={isLoadingInitial}
                />
            </PageHeader>
            <PageBody justifyContent={showEmptyState ? 'center' : 'flex-start'} alignItems='stretch'>
                {   
                    !showEmptyState
                        ? (
                            <TypeFilter
                                search={search}
                                selectedStatus={selectedStatus}
                                isMobile={isMobile}
                                mobileView={mobileView}
                                onFilterChange={handleFilterChange}
                                onMobileClose={handleMobileFilterClose}
                            >
                                <TiminatorTableV2 
                                    onRowClick={canEditProjectTypes ? onRowEdit : undefined} 
                                    columns={enhancedColumns}
                                    data={data}
                                    hasMore={hasMore}
                                    onLoadMore={loadNext}
                                    isLoadingMore={isLoadingMore} />
                            </TypeFilter>
                        )
                        : <IconTextLarge 
                            text={intl.formatMessage({id: Lx.Type.NO_PROJECT_TYPES})} 
                            subtext={intl.formatMessage({id: Lx.Type.CREATE_AND_USE_PROJECT_TYPE})} 
                            icon={<SVGS.TypesIcon />} />
                }
            </PageBody>
            {isMobile && mobileView === 'list' && canEditProjectTypes && 
                <PageFooter>
                    <CreateNewButtonV2 onClick={handleCreateNew} />
                </PageFooter>
            }
        </PageWrapperV2>
        <DeleteDialog 
            open={open} 
            onCancel={handleDeleteCancel} 
            onConfirmDelete={handleDeleteConfirm}
            deleteHeader={intl.formatMessage({id: Lx.Type.DELETE_PROJECT_TYPE})}
            deleteMessage={intl.formatMessage({id: Lx.Type.DELETE_PROJECT_TYPE_MESSAGE}, { name: selectedProjectType?.name })}
            title={intl.formatMessage({id: Lx.General.PROJECT_TYPE})}  />
    </>
}
